import * as React from 'react';

import type { RateSeniorityLevelDto } from '@/api/positioning-client';
import { usePositioningCouple } from '@/api/use-positioning';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { POSITIONING_ORANGE_PRESENTATION } from '@/lib/positioning-labels';

// =============================================================================
// positioning-cell.tsx — Indicateur de positionnement F-10 UNAIRE d'une cellule (Cycle 2, DBU §3.6).
// =============================================================================
// RÉVISION UNAIRE (G0-S17-16). Rend, pour un couple profil×niveau, l'indicateur UNAIRE — le DRAPEAU
// `orange` (TJM ≥ P75) calculé par l'API (autorité). Le front ne DÉRIVE plus rien, ne connaît plus
// les bornes P25/P75 et n'affiche plus de tooltip de bornes. Comportement strict :
//   • Indicateur MASQUÉ (effectif sous seuil, `indicator.masked === true`) ⇒ message NEUTRE, AUCUNE
//     borne, AUCUN effectif (G0-S17-3). Rien d'exploitable.
//   • Indicateur NON masqué et `orange === true` (TJM ≥ P75) ⇒ badge ORANGE DOUBLÉ d'un LIBELLÉ
//     « Au-dessus du marché » + ICÔNE (WCAG P-06 ; jamais la couleur seule).
//   • Indicateur NON masqué et `orange === false` (pas-orange) ⇒ AUCUN indicateur (rien à signaler).
//   • Aucun verdict pour ce couple (`undefined`, ou pas de TJM sauvegardé) ⇒ rien.
//
// MODÈLE PER-COUPLE (S17 réconciliation, option B) : la cellule émet SA PROPRE query verdictant son
// couple contre le `savedValue` SAUVEGARDÉ (committé par l'autosave, PAS la frappe). La query est
// keyée sur `[positioning, campaignId, profile, level, tjm]` (TanStack) : elle ne re-fire QUE quand
// la valeur SAUVEGARDÉE change, JAMAIS à chaque keystroke (ENF-01). Sans valeur sauvegardée
// (`savedValue === null`), AUCUNE requête n'est émise. La cellule ne DÉRIVE jamais le verdict : elle
// l'AFFICHE.

export interface PositioningCellProps {
  /** Ressource campagne (route GELÉE D1) ; `undefined` tant que la grille n'est pas chargée. */
  readonly campaignId: string | undefined;
  /** Cycle interrogé (Cycle 2 pour l'indicateur F-10). */
  readonly cycleNumber: number;
  readonly profileCode: string;
  readonly seniorityLevel: RateSeniorityLevelDto;
  /** TJM SAUVEGARDÉ du couple (autosave committé) ; `null` ⇒ rien à verdicter, aucune requête. */
  readonly savedValue: number | null;
  /** Activation F-10 (Cycle 2 actif + marché disponible) ; sinon aucune requête, rien rendu. */
  readonly active: boolean;
}

export function PositioningCell({
  campaignId,
  cycleNumber,
  profileCode,
  seniorityLevel,
  savedValue,
  active,
}: PositioningCellProps): React.JSX.Element | null {
  // Couple verdictable UNIQUEMENT s'il porte une valeur SAUVEGARDÉE (committée). La frappe en cours
  // n'est jamais transmise (ENF-01) : seul `savedValue` (post-autosave) alimente la requête.
  const couple =
    savedValue !== null ? { profileCode, seniorityLevel, tjmValueEur: savedValue } : undefined;

  const query = usePositioningCouple(campaignId, cycleNumber, couple, active);
  const indicator = query.data;

  // Aucun verdict connu pour ce couple (pas de saisie sauvegardée, ou couple absent du marché).
  if (indicator === undefined) {
    return null;
  }

  // Maquette #85 (point 11) : « Indicateur indisponible » SUPPRIMÉ partout. Un couple MASQUÉ (anonymat
  // insuffisant, G0-S17-3) n'affiche donc PLUS de message neutre — RIEN n'est rendu (aucune donnée
  // exploitable ne fuit, l'affordance de lecture marché reste gouvernée en amont par `marketReadingActive`).
  if (indicator.masked) {
    return null;
  }

  // UNAIRE (G0-S17-16) : seul l'état ORANGE (TJM ≥ P75) est affiché. Pas-orange ⇒ RIEN.
  if (!indicator.orange) {
    return null;
  }

  const { label, Icon, badgeClassName, description } =
    POSITIONING_ORANGE_PRESENTATION;

  return (
    <Badge
      variant="outline"
      className={cn('gap-1', badgeClassName)}
      // `title` = description anonymisée (aucune borne, aucun TJM individuel, aucune raison sociale).
      title={description}
    >
      {/* L'icône est décorative : le LIBELLÉ texte porte l'information (jamais la couleur seule). */}
      <Icon aria-hidden="true" className="h-3.5 w-3.5" />
      <span>{label}</span>
    </Badge>
  );
}
